/**
 * Pure helpers that turn a profile's `taxonomy` block into concrete output
 * columns + field rules. No DB access — this runs inside the mapper/exporter.
 *
 * Two outputs:
 *   - taxonomyFieldRules(profile): Record<column, Rule>  — synthetic rules that
 *     populate "Product Category", the Google category column, and each enabled
 *     attribute's metafield column.
 *   - taxonomyExtraColumns(profile): string[]            — the attribute columns
 *     that are NOT already part of the fixed Shopify schema, appended after the
 *     fixed columns in preview + export.
 *
 * Category VALUES are the full path string (set by the UI), not ids.
 */
import { shopifyTarget } from "./targets/shopify";
import type { Profile, Rule } from "./types";

const SHOPIFY_CATEGORY_COLUMN = "Product Category";
const GOOGLE_CATEGORY_COLUMN = "Google Shopping / Google Product Category";

/**
 * Taxonomy attributes that already have a dedicated column in Monjolika's fixed
 * Shopify export schema, where the metafield key differs from the taxonomy
 * handle. Reuse those columns instead of emitting a duplicate.
 */
const HANDLE_TO_FIXED_COLUMN: Record<string, string> = {
  color: "Color (product.metafields.shopify.color-pattern)",
  fabric: "Fabric (product.metafields.shopify.fabric)",
  size: "Size (product.metafields.shopify.size)",
  "age-group": "Age group (product.metafields.shopify.age-group)",
  "target-gender": "Target gender (product.metafields.shopify.target-gender)",
};

const FIXED_COLUMNS = new Set<string>(shopifyTarget.columns);

/** The Shopify CSV column header an attribute writes into. */
export function attributeColumnHeader(name: string, handle: string): string {
  return HANDLE_TO_FIXED_COLUMN[handle] ?? `${name} (product.metafields.shopify.${handle})`;
}

/**
 * Synthetic field rules contributed by a profile's taxonomy block, keyed by
 * column header. In withTaxonomy these are the DEFAULT for each taxonomy column;
 * an explicit manual `profile.fields[col]` rule (e.g. a Dictionary module the
 * user added to the column in the grid) takes precedence. So taxonomy fills in
 * the columns the user has not customised, while still letting them transform a
 * metafield value (the inline preview reads the manual rule too, so they match).
 */
export function taxonomyFieldRules(profile: Profile): Record<string, Rule> {
  const tax = profile.taxonomy;
  if (!tax) return {};
  const out: Record<string, Rule> = {};
  if (tax.shopify?.categoryName) {
    out[SHOPIFY_CATEGORY_COLUMN] = { kind: "static", value: tax.shopify.categoryName };
  }
  if (tax.google?.categoryName) {
    out[GOOGLE_CATEGORY_COLUMN] = { kind: "static", value: tax.google.categoryName };
  }
  for (const attr of tax.attributes ?? []) {
    if (!attr.enabled) continue;
    out[attributeColumnHeader(attr.name, attr.handle)] = attr.rule;
  }
  return out;
}

/**
 * Extra (non-fixed) columns the taxonomy block introduces, in stable order.
 * These are appended after the target's fixed columns in preview + export.
 */
export function taxonomyExtraColumns(profile: Profile): string[] {
  const tax = profile.taxonomy;
  if (!tax?.attributes) return [];
  const cols: string[] = [];
  for (const attr of tax.attributes) {
    if (!attr.enabled) continue;
    const col = attributeColumnHeader(attr.name, attr.handle);
    if (!FIXED_COLUMNS.has(col) && !cols.includes(col)) cols.push(col);
  }
  return cols;
}

/**
 * Return a profile whose `fields` include the taxonomy-derived rules. When the
 * profile has no taxonomy block the original object is returned unchanged.
 */
export function withTaxonomy(profile: Profile): Profile {
  const rules = taxonomyFieldRules(profile);
  if (Object.keys(rules).length === 0) return profile;
  // taxonomy rules first = default; manual profile.fields override per column.
  return { ...profile, fields: { ...rules, ...profile.fields } };
}
